// Considered: No existing molecule provides a root-kind selector (sphere/schema_type/query) with a read-only lock banner for saved contexts. See spec Reuse Audit.
import React, { useState } from 'react';
import { useTranslation } from 'localization';
import type { ContextFlowRoot } from '../../../types/contextFlow';
import './ContextRootInspectorMolecule.scss';

export interface ContextRootInspectorMoleculeProps {
  root: ContextFlowRoot;
  onChange: (root: ContextFlowRoot) => void;
  /** True on saved contexts — root cannot be changed after creation */
  readOnly: boolean;
  /** Fired by the lock banner's "Clone" link — parent wires it to Save as copy. */
  onCloneClick?: () => void;
}

const ROOT_KINDS = [
  { id: 'sphere' as const, icon: 'pi-circle-on' },
  { id: 'schema_type' as const, icon: 'pi-tag' },
  { id: 'query' as const, icon: 'pi-filter' },
];

const ContextRootInspectorMolecule: React.FC<ContextRootInspectorMoleculeProps> = ({
  root,
  onChange,
  readOnly,
  onCloneClick,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ContextFlowRoot['rootType']>(root.rootType);

  const handleKindSelect = (kind: ContextFlowRoot['rootType']) => {
    if (readOnly) return;
    setMode(kind);
    onChange({ ...root, rootType: kind });
  };

  return (
    <div className="context-root-inspector-molecule">
      {/* Inspector header */}
      <div className="context-root-inspector-molecule__header">
        <span className="context-root-inspector-molecule__icon" aria-hidden="true">
          <i className="pi pi-circle-on" />
        </span>
        <div className="context-root-inspector-molecule__title-group">
          <div className="context-root-inspector-molecule__eyebrow">
            {t('context.editor.inspector.root.eyebrow')}
          </div>
          <div className="context-root-inspector-molecule__title">
            {t('context.editor.inspector.root.title')}
            {readOnly && (
              <i className="pi pi-lock context-root-inspector-molecule__lock-icon" aria-label={t('context.editor.inspector.root.locked')} />
            )}
          </div>
        </div>
      </div>

      {/* Lock banner — only on saved context */}
      {readOnly && (
        <div
          className="context-root-inspector-molecule__lock-banner"
          data-testid="root-lock-banner"
          role="note"
        >
          <i className="pi pi-lock" aria-hidden="true" />
          <span>
            {t('context.editor.inspector.root.lockBanner')}{' '}
            <button
              type="button"
              className="context-root-inspector-molecule__clone-link"
              onClick={onCloneClick}
              disabled={!onCloneClick}
              data-testid="root-lock-banner-clone-cta"
            >
              {t('context.editor.inspector.root.cloneCta')}
            </button>
          </span>
        </div>
      )}

      {/* Kind options — disabled when readOnly */}
      <div
        className={`context-root-inspector-molecule__body${readOnly ? ' context-root-inspector-molecule__body--locked' : ''}`}
        data-testid="root-inspector-body"
        style={readOnly ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
      >
        <div className="context-root-inspector-molecule__kind-label">
          {t('context.editor.inspector.root.kindLabel')}
        </div>
        {ROOT_KINDS.map((k) => {
          const active = mode === k.id;
          return (
            <button
              key={k.id}
              type="button"
              className={`context-root-inspector-molecule__kind-btn${active ? ' context-root-inspector-molecule__kind-btn--active' : ''}`}
              data-testid={`root-kind-btn-${k.id}`}
              onClick={() => handleKindSelect(k.id)}
              aria-pressed={active}
              tabIndex={readOnly ? -1 : 0}
            >
              <span className="context-root-inspector-molecule__kind-icon" aria-hidden="true">
                <i className={`pi ${k.icon}`} />
              </span>
              <div className="context-root-inspector-molecule__kind-text">
                <div className="context-root-inspector-molecule__kind-name">
                  {t(`context.editor.inspector.root.kind.${k.id}.label`)}
                </div>
                <div className="context-root-inspector-molecule__kind-desc">
                  {t(`context.editor.inspector.root.kind.${k.id}.desc`)}
                </div>
              </div>
              {active && <i className="pi pi-check context-root-inspector-molecule__kind-check" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ContextRootInspectorMolecule;
